import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, CheckCircle2, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function fmtCurrency(n) {
  if (n == null) return "—";
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtCount(n) {
  if (n == null) return "—";
  return n.toLocaleString();
}
function fmtPct(n) {
  if (n == null) return "—";
  return `${n}%`;
}

/**
 * Dialog that lets a GM/Owner upload the Zenoti end-of-day report email
 * (either the raw .eml file or the pasted email HTML), previews the parsed
 * KPIs, and commits them so the Pulse tab picks them up.
 */
export default function DailyReportImportDialog({ open, onClose, defaultBoutique, onImported }) {
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [html, setHtml] = useState("");
  const inputRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    if (defaultBoutique) fd.append("boutique_hint", defaultBoutique);
    try {
      const r = await api.post("/daily-reports/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success(`Parsed report for ${r.data.date}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't parse this report");
    } finally { setBusy(false); }
  };

  const doPaste = async () => {
    if (!html.trim()) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("html", html);
    if (defaultBoutique) fd.append("boutique_hint", defaultBoutique);
    try {
      const r = await api.post("/daily-reports/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success(`Parsed report for ${r.data.date}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't parse this report");
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      await api.post("/daily-reports/commit", { report: preview });
      toast.success("Saved to Pulse");
      onImported?.(preview);
      setPreview(null); setHtml(""); setPasteMode(false);
      if (inputRef.current) inputRef.current.value = "";
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto mobile-modal-wrap" data-testid="dailyreport-dialog">
      <div className="bg-white rounded-3xl w-full max-w-4xl card-shadow my-8 mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="px-8 py-5 border-b border-stone-100 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Zenoti daily report</div>
            <div className="font-serif-display text-2xl text-stone-900 mt-1">Import today's numbers</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { setPasteMode((v) => !v); setPreview(null); }} className="text-[11px] px-3 py-1.5 rounded-full text-stone-500 hover:bg-stone-100" data-testid="toggle-paste">
              {pasteMode ? "Upload .eml" : "Paste HTML instead"}
            </button>
            <button onClick={onClose} data-testid="dailyreport-close" className="p-2 rounded-full hover:bg-stone-100">
              <X size={18} strokeWidth={1.25} className="text-stone-500" />
            </button>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {!preview && !pasteMode && (
            <section
              onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) doUpload(f); }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              data-testid="dailyreport-dropzone"
              className={`rounded-3xl border-2 border-dashed transition-all p-14 text-center cursor-pointer ${dragOver ? "border-stone-400 bg-stone-50/70" : "border-stone-200 hover:border-stone-300"}`}
              onClick={() => inputRef.current?.click()}
            >
              <input ref={inputRef} type="file" accept=".eml,.html,.htm" className="hidden" data-testid="dailyreport-file"
                onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])} />
              <div className="w-14 h-14 rounded-full bg-stone-50 border border-stone-100 mx-auto flex items-center justify-center mb-4">
                <Upload size={20} strokeWidth={1.25} className="text-stone-500" />
              </div>
              <div className="font-serif-display text-xl text-stone-800">
                {busy ? "Reading your report…" : "Drop the Zenoti daily report email"}
              </div>
              <div className="text-xs text-stone-400 mt-2">.eml file — or use the "Paste HTML" option above</div>
            </section>
          )}

          {!preview && pasteMode && (
            <section className="space-y-3" data-testid="dailyreport-paste">
              <textarea
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                rows={10}
                placeholder="Paste the full raw HTML of the daily-report email here…"
                className="w-full px-4 py-3 rounded-2xl subtle-border text-xs font-mono outline-none focus:border-stone-400"
              />
              <div className="text-right">
                <button onClick={doPaste} disabled={busy || !html.trim()} data-testid="dailyreport-paste-parse" className="px-5 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40">
                  {busy ? "Parsing…" : "Parse HTML"}
                </button>
              </div>
            </section>
          )}

          {preview && (
            <section className="space-y-4" data-testid="dailyreport-preview">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="label-eyebrow">Preview</div>
                  <div className="font-serif-display text-xl text-stone-900 mt-1">
                    {preview.boutique_code} · {preview.date}
                  </div>
                  <div className="text-xs text-stone-400 mt-1 truncate max-w-md">
                    {preview.email_subject || "Manual paste"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setPreview(null); setHtml(""); if (inputRef.current) inputRef.current.value = ""; }} className="px-4 py-2 rounded-full text-sm text-stone-600 hover:bg-stone-100" data-testid="dailyreport-cancel">Cancel</button>
                  <button onClick={commit} disabled={busy} className="px-5 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40" data-testid="dailyreport-commit">
                    <CheckCircle2 size={14} strokeWidth={1.5} className="inline mr-1" />
                    {busy ? "Saving…" : "Save to Pulse"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatMini label="Total revenue" value={fmtCurrency(preview.sales?.total)} />
                <StatMini label="Services" value={`${fmtCurrency(preview.sales?.services?.amount)} · ${preview.sales?.services?.count ?? "—"}`} />
                <StatMini label="Memberships sold" value={fmtCount(preview.sales?.memberships?.count)} />
                <StatMini label="Gift cards" value={fmtCurrency(preview.sales?.gift_cards?.amount)} />
                <StatMini label="New guests" value={fmtCount(preview.guests?.new_count)} />
                <StatMini label="Existing guests" value={fmtCount(preview.guests?.existing_count)} />
                <StatMini label="Rebook rate" value={fmtPct(preview.guests?.rebook_pct)} />
                <StatMini label="Utilization" value={fmtPct(preview.guests?.center_utilization_employee_pct)} />
                <StatMini label="On-time" value={fmtCount(preview.employees?.on_time)} />
                <StatMini label="Late" value={fmtCount(preview.employees?.late)} />
                <StatMini label="Cancellations" value={fmtCount(preview.guests?.cancellations)} />
                <StatMini label="Walk-ins" value={fmtCount(preview.guests?.walkins)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-4 rounded-2xl bg-stone-50/60 border border-stone-100">
                  <div className="label-eyebrow mb-1">Top MTD therapist</div>
                  <div className="text-sm text-stone-800">{preview.employees?.top_service_therapist || "—"}</div>
                </div>
                <div className="p-4 rounded-2xl bg-stone-50/60 border border-stone-100">
                  <div className="label-eyebrow mb-1">Projected monthly revenue</div>
                  <div className="text-sm text-stone-800">{fmtCurrency(preview.revenue?.projected_monthly)}</div>
                </div>
              </div>

              <div className="text-[11px] text-stone-400 flex items-center gap-2">
                <AlertCircle size={11} strokeWidth={1.5} />
                {preview.mtd_sales?.length || 0} days of month-to-date sales included for the Pulse trend.
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="p-3 rounded-2xl bg-stone-50/60 border border-stone-100">
      <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">{label}</div>
      <div className="text-sm text-stone-900">{value}</div>
    </div>
  );
}
